import React, { use } from 'react';
import { useTranslation } from 'react-i18next';
import CommentOutlined from '@mui/icons-material/CommentOutlined';
import moment from 'moment';
import FullPageLoadingIndicator from 'shared/components/loading-indicator/full-page-loading-indicator';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import PageHeader from 'shared/components/page-header/page-header';
import type { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import type RowItem from 'shared/components/table/types/row-item';
import type { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';

import MonitoringBar from 'features/tenants/components/monitoring-bar/monitoring-bar';
import TenantDetails from 'features/tenants/components/tenant-details/tenant-details';
import TableColumns from 'features/tenants/enums/table-colums';
import useTenantsTable from 'features/tenants/hooks';
import { AggregatedType } from 'features/tenants/models/aggregated-monitor-data';
import { Tenant } from 'features/tenants/models/tenant';
import TenantsProviderContext from 'features/tenants/providers/tenants-provider/contexts/tenants-provider-context';
import styles from './tenants.module.scss';

const Tenants = (): React.JSX.Element | null => {
  const { t } = useTranslation('tenants');

  const { readTenantsData, readTenantsIsSuccess, readTenantsIsLoading, isTenantPopupOpen, accessTenantIsLoading, openDetails } =
    use(TenantsProviderContext);

  const { sortedTenants, sort, handleSortChanged, overflowMenuItems } = useTenantsTable();

  const columns: ColumnDefinition<Tenant, NestedKeyof<Tenant>>[] = [
    { key: TableColumns.DisplayName, label: t('tenantTable.column.name') },
    {
      key: TableColumns.GatewayMonitorings,
      label: t('tenantTable.column.connectorsAndGateway'),
      renderCustomContentProvider: ({ connectorMonitorings, gatewayMonitorings }) =>
        connectorMonitorings || gatewayMonitorings ? (
          <MonitoringBar
            monitoring={(connectorMonitorings ?? []).concat(gatewayMonitorings ?? [])}
            aggregatedType={AggregatedType.ConnectorsAndGateways}
          />
        ) : (
          <>{t('tenantTable.monitoring.noData')}</>
        ),
      disableSort: true,
    },
    {
      key: TableColumns.SystemMonitorings,
      label: t('tenantTable.column.systems'),
      renderCustomContentProvider: ({ systemMonitorings }) =>
        systemMonitorings ? (
          <MonitoringBar monitoring={systemMonitorings} aggregatedType={AggregatedType.Systems} />
        ) : (
          <>{t('tenantTable.monitoring.noData')}</>
        ),
      disableSort: true,
    },
    {
      key: TableColumns.DevicesMonitorings,
      label: t('tenantTable.column.devices'),
      renderCustomContentProvider: ({ devicesMonitorings }) =>
        devicesMonitorings ? (
          <MonitoringBar monitoring={devicesMonitorings} aggregatedType={AggregatedType.Devices} />
        ) : (
          <>{t('tenantTable.monitoring.noData')}</>
        ),
      disableSort: true,
    },
    {
      key: TableColumns.Note,
      label: '',
      renderCustomContentProvider: ({ note }) => <CommentOutlined sx={{ visibility: note ? 'visible' : 'hidden', fontSize: 16 }} />,
      disableSort: true,
      tableCellProps: {
        align: 'left',
        width: '40px',
      },
    },
  ];

  const lastUpdate =
    readTenantsData?.lastUpdatedOn &&
    `${t('tenantDetails.lastUpdated')} ${moment(readTenantsData.lastUpdatedOn).format('DD-MM-YYYY HH:mm')}`;

  const renderLoadingState = (): React.JSX.Element | null => {
    if (!readTenantsIsLoading) return null;
    return <LoadingIndicator />;
  };

  const renderContent = (): React.JSX.Element | null => {
    if (!readTenantsIsSuccess || !readTenantsData) return null;

    return (
      <Table
        data={sortedTenants.map<RowItem<Tenant>>((tenant: Tenant) => ({ data: tenant }))}
        onItemClick={(item: Tenant) => openDetails('viewing', item)}
        columns={columns}
        overflowMenuOptions={overflowMenuItems}
        rowIdentifier={(item: Tenant) => item.realmName}
        onSortChanged={handleSortChanged}
        initialOrderBy={sort.property}
        initialOrderDirection={sort.isAscending ? 'asc' : 'desc'}
      />
    );
  };

  const renderTenantDetails = (): React.JSX.Element | null => {
    if (!isTenantPopupOpen) return null;
    return <TenantDetails />;
  };

  return (
    <>
      <div className={styles.container}>
        <PageHeader title={t('pageTitle')} description={lastUpdate} />
        {renderLoadingState()}
        {renderContent()}
        {renderTenantDetails()}
      </div>
      <FullPageLoadingIndicator isLoading={accessTenantIsLoading} useTimeout={false} />
    </>
  );
};

export default Tenants;
